import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Empty,
  Tag,
  Tabs,
  Space,
  Spin,
  Select,
} from "antd";
import { FileTextOutlined, BookOutlined } from "@ant-design/icons";
import Utilities from "../../dependencies/custom/react-utilities";
import useTable from "../../hooks/table";
import Settings from "../../dependencies/custom/settings";
import dayjs from "dayjs";

const { Title, Text } = Typography;

function withCacheBuster(url) {
  if (!url) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${Date.now()}`;
}

export default function MyReports() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [applications, setApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState();
  const [selectedApplication, setSelectedApplication] = useState();
  const [selectedStudent, setSelectedStudent] = useState();
  const [circulars, setCirculars] = useState([]);
  const [packages, setPackages] = useState([]);
  const [commsLoading, setCommsLoading] = useState(false);

  const table = useTable(
    { pagination: { current: 1, pageSize: 20, hideOnSinglePage: true } },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  useEffect(() => {
    setShow(applications?.some((v) => v?.status?.toLowerCase() === "admitted"));
    table.setColumns([
      { title: "Name", dataIndex: "student_name" },
      { title: "Term", dataIndex: "term" },
      { title: "Academic Year", dataIndex: "academicYear" },
      {
        title: "Date",
        dataIndex: "generatedAt",
        render: (v) => dayjs(v)?.format("DD/MM/YYYY"),
      },
      {
        title: "Report",
        key: "report",
        render: (v, record) => (
          <Button
            icon={<i className="fas fa-file" />}
            type="primary"
            onClick={() =>
              Utilities.openPopup(withCacheBuster(record?.reportFile))
            }
          >
            View
          </Button>
        ),
      },
    ]);
  }, [applications]);

  useEffect(() => {
    bootstrap();
  }, []);

  async function bootstrap() {
    const res = await Utilities.fetchWithAuth(
      "GET",
      Settings.urls.enrollment?.related_applications,
      undefined,
      navigate,
    );
    if (res?.ok && applications?.length <= 0) {
      setApplications(res?.data);
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  }

  const admittedApplications =
    applications?.filter((app) => app?.status?.toLowerCase() === "admitted") ||
    [];

  function handleChildChange(admissionID) {
    const application = admittedApplications.find(
      (app) => app.admissionID === admissionID,
    );
    setSelectedApplicationId(admissionID);
    setSelectedApplication(application);
    setSelectedStudent(undefined);
    table.setData([]);
    setCirculars([]);
    setPackages([]);
  }

  async function processSelectedChild() {
    if (!selectedApplication) {
      Utilities.showNotification(undefined, "Select a child first");
      return;
    }
    await getDetails(selectedApplication);
  }

  async function getDetails(child) {
    if (!child?.student) return;
    const student = child.student;
    setSelectedStudent(student);
    const studentID = student.id;

    // Reset
    table.setData([]);
    setCirculars([]);
    setPackages([]);

    // Fetch reports and communications in parallel
    table.setLoading(true);
    setCommsLoading(true);

    const [reportsRes, circularsRes, packagesRes] = await Promise.all([
      Utilities.fetchWithAuth(
        "GET",
        `${Settings.urls.academics.student_academic_report}/${studentID}`,
        undefined,
        navigate,
      ),
      Utilities.fetchWithAuth(
        "GET",
        `${Settings.urls.academics.studentCommunications.replace(":studentID", studentID)}?type=circulars`,
        undefined,
        navigate,
      ),
      Utilities.fetchWithAuth(
        "GET",
        `${Settings.urls.academics.studentCommunications.replace(":studentID", studentID)}?type=packages`,
        undefined,
        navigate,
      ),
    ]);

    if (reportsRes?.ok) {
      table.setData(reportsRes.data);
    } else {
      Utilities.showNotification(undefined, reportsRes?.msg);
    }

    if (circularsRes?.ok) {
      setCirculars(circularsRes.data?.circulars || []);
    }

    if (packagesRes?.ok) {
      setPackages(packagesRes.data?.holidayPackages || []);
    }

    table.setLoading(false);
    setCommsLoading(false);
  }

  const studentLabel = selectedStudent
    ? `${selectedStudent.firstName} ${selectedStudent.lastName}`
    : null;

  const tabItems = [
    {
      key: "reports",
      label: (
        <Space>
          <FileTextOutlined />
          Term Reports
        </Space>
      ),
      children: table.table,
    },
    {
      key: "circulars",
      label: (
        <Space>
          <i className="fas fa-envelope-open-text" />
          Circulars
          {circulars.length > 0 && (
            <Tag color="green" style={{ marginLeft: 4 }}>
              {circulars.length}
            </Tag>
          )}
        </Space>
      ),
      children: commsLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : circulars.length === 0 ? (
        <Empty description="No circulars available" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  borderBottom: "1px solid #f0f0f0",
                  background: "#fafafa",
                }}
              >
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Title
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Audience
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Term
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {circulars.map((circular) => (
                <tr
                  key={circular.id}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <td style={{ padding: "12px 16px" }}>
                    <Text strong>{circular.title}</Text>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <Tag
                      color={
                        circular.audience === "Whole School" ? "green" : "blue"
                      }
                    >
                      {circular.audience || "—"}
                    </Tag>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {circular.term ? <Tag>{circular.term}</Tag> : "—"}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {circular.files?.length > 0 ? (
                      <Space wrap>
                        {circular.files.map((file) => (
                          <Button
                            key={file.id}
                            type="primary"
                            icon={<i className="fas fa-file" />}
                            onClick={() => Utilities.openPopup(file.fileUrl)}
                          >
                            View
                          </Button>
                        ))}
                      </Space>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    },
    {
      key: "packages",
      label: (
        <Space>
          <BookOutlined />
          Holiday Packages
          {packages.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {packages.length}
            </Tag>
          )}
        </Space>
      ),
      children: commsLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : packages.length === 0 ? (
        <Empty description="No holiday packages available" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr
                style={{
                  borderBottom: "1px solid #f0f0f0",
                  background: "#fafafa",
                }}
              >
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Title
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Subject
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Term
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {packages.map((pkg) => (
                <tr key={pkg.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "12px 16px" }}>
                    <Text strong>{pkg.title}</Text>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {pkg.subjectName ? (
                      <Tag color="orange">{pkg.subjectName}</Tag>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {pkg.term ? <Tag>{pkg.term}</Tag> : "—"}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {pkg.files?.length > 0 ? (
                      <Space wrap>
                        {pkg.files.map((file) => (
                          <Button
                            key={file.id}
                            type="primary"
                            icon={<i className="fas fa-file" />}
                            onClick={() => Utilities.openPopup(file.fileUrl)}
                          >
                            View
                          </Button>
                        ))}
                      </Space>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-3">
        <Title level={3}>My Report</Title>
        <Text type="secondary">
          {show
            ? "Select a child to view their reports, circulars and holiday packages"
            : "No child has been admitted for academic report"}
        </Text>
      </div>

      {show && (
        <>
          <Card className="rounded-2xl shadow mt-6 mb-3">
            <Row gutter={[12, 12]} align="bottom">
              <Col xs={24} md={14} lg={12}>
                <Text strong>Select Child</Text>
                <Select
                  placeholder="Choose a child"
                  value={selectedApplicationId}
                  onChange={handleChildChange}
                  style={{ width: "100%", marginTop: 6 }}
                  showSearch
                  optionFilterProp="label"
                  options={admittedApplications.map((app) => ({
                    value: app.admissionID,
                    label: `${app.student?.firstName || ""} ${app.student?.lastName || ""} (${app.student?.className || "No class"})`,
                  }))}
                />
              </Col>
              <Col xs={24} md={8} lg={6}>
                <Button
                  onClick={processSelectedChild}
                  icon={<i className="fas fa-eye" />}
                  type="primary"
                  disabled={!selectedApplicationId}
                  loading={table.loading || commsLoading}
                  block
                >
                  Get Reports
                </Button>
              </Col>
            </Row>
          </Card>

          <style>{`
                        .chrome-tabs .ant-tabs-nav {
                            margin-bottom: 0;
                        }
                        .chrome-tabs .ant-tabs-nav::before {
                            border-bottom: none !important;
                        }
                        .chrome-tabs .ant-tabs-tab {
                            border-radius: 10px 10px 0 0 !important;
                            border: 1px solid #d0d0d0 !important;
                            border-bottom: none !important;
                            background: #e8eaed !important;
                            margin: 0 3px 0 0 !important;
                            padding: 10px 24px !important;
                            font-size: 14.5px !important;
                            font-weight: 500 !important;
                            color: #5f6368 !important;
                            transition: background 0.15s !important;
                            position: relative;
                            bottom: -1px;
                        }
                        .chrome-tabs .ant-tabs-tab:hover {
                            background: #f1f3f4 !important;
                            color: #202124 !important;
                        }
                        .chrome-tabs .ant-tabs-tab-active {
                            background: #ffffff !important;
                            border-color: #d0d0d0 !important;
                            z-index: 2;
                        }
                        .chrome-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
                            color: #1a73e8 !important;
                            font-weight: 600 !important;
                        }
                        .chrome-tabs .ant-tabs-content-holder {
                            border: 1px solid #d0d0d0;
                            border-radius: 0 4px 4px 4px;
                            padding: 20px;
                            background: #fff;
                        }
                        .chrome-tabs .ant-tabs-ink-bar {
                            display: none !important;
                        }
                    `}</style>
          <Card
            title={
              studentLabel
                ? `${studentLabel} (${selectedStudent?.id})`
                : "Select a child and get reports"
            }
            className="rounded-2xl shadow criclebox tablespace"
          >
            <Tabs
              defaultActiveKey="reports"
              items={tabItems}
              className="chrome-tabs"
              type="card"
            />
          </Card>
        </>
      )}
    </div>
  );
}
